import { useCallback } from 'react';
import { create } from 'zustand';
export const QuizMeDefaultState = {
  LESSON: {},
  SHORT_FORM_CONTENT: {},
  TRACK: {}
};
export const useQuizMeSessionConfig = create(set => Object.assign({}, QuizMeDefaultState, {
  deleteQuizState: ({
    contentType,
    contentId
  }) => {
    set(state => {
      const filteredState = Object.assign({}, state[contentType]);
      delete filteredState[contentId];
      return {
        [contentType]: filteredState
      };
    });
  },
  incrementQuestionWithAnswer: ({
    contentType,
    contentId,
    correct
  }) => {
    set(state => {
      const newState = Object.assign({}, state[contentType][contentId], {
        questionIndex: state[contentType][contentId].questionIndex + 1,
        correctAnswers: state[contentType][contentId].correctAnswers + (correct ? 1 : 0)
      });
      return {
        [contentType]: Object.assign({}, state[contentType], {
          [contentId]: newState
        })
      };
    });
  },
  initialiseQuiz: ({
    contentType,
    contentId
  }) => {
    set(state => ({
      [contentType]: Object.assign({}, state[contentType], {
        [contentId]: {
          questionIndex: 0,
          correctAnswers: 0
        }
      })
    }));
  },
  setQuizMeStatus: ({
    contentType,
    contentId,
    quizMeStatus
  }) => {
    set(state => ({
      [contentType]: Object.assign({}, state[contentType], {
        [contentId]: Object.assign({}, state[contentType][contentId], {
          quizMeStatus
        })
      })
    }));
  },
  setQuizMeTasks: ({
    contentType,
    videoContentId,
    tasks
  }) => {
    set(state => ({
      [contentType]: Object.assign({}, state[contentType], {
        [videoContentId]: Object.assign({}, state[contentType][videoContentId], {
          tasks
        })
      })
    }));
  },
  resetQuiz: ({
    contentType,
    videoContentId
  }) => {
    set(state => ({
      [contentType]: Object.assign({}, state[contentType], {
        [videoContentId]: Object.assign({}, state[contentType][videoContentId], {
          questionIndex: 0,
          correctAnswers: 0
        })
      })
    }));
  }
}));
export const useQuizMeSession = () => {
  const {
    LESSON,
    SHORT_FORM_CONTENT,
    TRACK,
    deleteQuizState,
    incrementQuestionWithAnswer,
    initialiseQuiz,
    resetQuiz,
    setQuizMeTasks,
    setQuizMeStatus
  } = useQuizMeSessionConfig();
  const getQuizState = useCallback((id, contentType) => {
    switch (contentType) {
      case 'LESSON':
        return LESSON[id];
      case 'SHORT_FORM_CONTENT':
        return SHORT_FORM_CONTENT[id];
      case 'TRACK':
        return TRACK[id];
      default:
        return undefined;
    }
  }, [LESSON, SHORT_FORM_CONTENT, TRACK]);
  return {
    deleteQuizState,
    getQuizState,
    incrementQuestionWithAnswer,
    initialiseQuiz,
    resetQuiz,
    setQuizMeTasks,
    setQuizMeStatus
  };
};
const flattenQuizQuestionsWithCorrelation = ({
  lessonContentId,
  quizTasks
}) => {
  if (quizTasks) {
    return quizTasks.tasks.flatMap(quizMeTaskItem => {
      var _quizMeTaskItem$quizR;
      return ((_quizMeTaskItem$quizR = quizMeTaskItem.quizResponse) === null || _quizMeTaskItem$quizR === void 0 || (_quizMeTaskItem$quizR = _quizMeTaskItem$quizR.quiz) === null || _quizMeTaskItem$quizR === void 0 ? void 0 : _quizMeTaskItem$quizR.questions.map(question => {
        var _quizMeTaskItem$quizR2;
        return Object.assign({}, question, {
          correlationId: (_quizMeTaskItem$quizR2 = quizMeTaskItem.quizResponse) === null || _quizMeTaskItem$quizR2 === void 0 ? void 0 : _quizMeTaskItem$quizR2.correlationId,
          contentId: lessonContentId
        });
      })) || [];
    });
  }
  return [];
};
export const useQuizMeHelpers = () => {
  const {
    getQuizState
  } = useQuizMeSession();
  const formatAsyncQuizMeQuestions = useCallback(({
    lessonContentId,
    contentType,
    initialQuizTasks,
    primaryContentId
  }) => {
    var _quizQuestions$questi;
    const quizSession = getQuizState(lessonContentId !== null && lessonContentId !== void 0 ? lessonContentId : primaryContentId, contentType);
    const quizQuestions = flattenQuizQuestionsWithCorrelation({
      lessonContentId,
      quizTasks: initialQuizTasks
    });
    const questionIndex = quizSession === null || quizSession === void 0 ? void 0 : quizSession.questionIndex;
    const currentCorrelationId = questionIndex !== undefined ? (_quizQuestions$questi = quizQuestions[questionIndex]) === null || _quizQuestions$questi === void 0 ? void 0 : _quizQuestions$questi.correlationId : undefined;
    return {
      currentCorrelationId,
      quizQuestions
    };
  }, [getQuizState]);
  return {
    formatAsyncQuizMeQuestions
  };
};
export const checkQuizMePollingStatus = (asyncQuizMe, hasError) => {
  if (!asyncQuizMe) {
    return {
      quizReady: false,
      pollingLoading: true,
      retryGeneration: false
    };
  }
  const {
    quizReady,
    pollingLoading,
    retryGeneration
  } = asyncQuizMe.tasks.reduce((prev, current) => {
    if (current.status === 'NOT_FOUND' || hasError) {
      prev.retryGeneration = true;
    }
    if (current.status !== 'COMPLETED' && current.status !== 'FAILED') {
      prev.quizReady = false;
    }
    return prev;
  }, {
    quizReady: true,
    pollingLoading: false,
    retryGeneration: false
  });
  return {
    quizReady,
    pollingLoading,
    retryGeneration
  };
};